import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Container,
  Grid,
  InputAdornment,
  Link as MuiLink,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { TasteSectionCard } from '@/components/Common/TasteSectionCard';
import { useAppLayout } from '@/hooks/useAppLayout';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  COSTS_SECTION_ID,
  costsSearchText,
  guideSearchText,
  SUPPORT_EMAIL,
  SUPPORT_EMAIL_HREF,
  SUPPORT_PHONE,
  SUPPORT_PHONE_HREF,
  supportCosts,
  supportGuideGroups,
  type SupportGuide,
} from './supportContent';

const NAV_RAIL_GAP = 24;

/**
 * Nav clicks fall through to native anchor behavior; the header offset comes
 * from `scroll-margin-top` on each section. This only covers the deep-link
 * case, where the hash is read before the sections have rendered.
 */
const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ block: 'start' });
};

/**
 * Numbered steps. Tailwind preflight strips `list-style`, so the markers are
 * drawn from a CSS counter instead of relying on the browser default.
 */
const StepList = ({ guide }: { guide: SupportGuide }) => (
  <Box
    component="ol"
    sx={{
      listStyle: 'none',
      m: 0,
      p: 0,
      counterReset: 'support-step',
      display: 'flex',
      flexDirection: 'column',
      gap: 1.75,
    }}
  >
    {guide.steps.map((step) => (
      <Box
        component="li"
        key={step.text}
        sx={{
          counterIncrement: 'support-step',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.75,
          '&::before': {
            content: 'counter(support-step)',
            flex: '0 0 auto',
            mt: '2px',
            width: 26,
            height: 26,
            display: 'grid',
            placeItems: 'center',
            borderRadius: '50%',
            fontFamily: 'var(--font-display)',
            fontSize: '0.75rem',
            fontWeight: 700,
            lineHeight: 1,
            color: 'var(--primary-navy)',
            backgroundColor:
              'color-mix(in srgb, var(--primary-fixed) 75%, transparent)',
          },
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body1"
            sx={{ color: 'var(--on-surface-variant)', lineHeight: 1.7 }}
          >
            {step.text}
          </Typography>
          {step.link?.href ? (
            <MuiLink
              href={step.link.href}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.75,
                mt: 0.75,
                fontSize: '0.875rem',
                fontWeight: 600,
                color: 'var(--primary-navy)',
              }}
            >
              {step.link.label}
              <i className="fa-light fa-arrow-right" aria-hidden="true" />
            </MuiLink>
          ) : null}
        </Box>
      </Box>
    ))}
  </Box>
);

const SectionCard = ({
  id,
  icon,
  title,
  summary,
  scrollOffset,
  children,
}: {
  id: string;
  icon: string;
  title: string;
  summary: string;
  scrollOffset: number;
  children: React.ReactNode;
}) => (
  <TasteSectionCard
    id={id}
    component="section"
    aria-labelledby={`${id}-heading`}
    sx={{
      p: { xs: 2.5, md: 4 },
      backgroundColor: 'var(--surface-container-lowest)',
      scrollMarginTop: `${scrollOffset}px`,
    }}
  >
    <Stack direction="row" spacing={2} sx={{ mb: 2.5 }}>
      <Box
        aria-hidden="true"
        sx={{
          flex: '0 0 auto',
          width: 44,
          height: 44,
          display: 'grid',
          placeItems: 'center',
          borderRadius: '12px',
          fontSize: '1.125rem',
          color: 'var(--primary-navy)',
          background:
            'linear-gradient(135deg, color-mix(in srgb, var(--primary-fixed) 85%, transparent) 0%, color-mix(in srgb, var(--surface-container-high) 60%, transparent) 100%)',
        }}
      >
        <i className={`fa-light ${icon}`} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          id={`${id}-heading`}
          variant="h5"
          component="h2"
          sx={{
            fontFamily: 'var(--font-display)',
            fontSize: { xs: '1.125rem', md: '1.25rem' },
            fontWeight: 700,
            letterSpacing: '-0.01em',
            color: 'var(--primary-navy)',
          }}
        >
          {title}
        </Typography>
        <Typography
          variant="body2"
          sx={{ mt: 0.5, color: 'var(--on-surface-variant)', lineHeight: 1.6 }}
        >
          {summary}
        </Typography>
      </Box>
    </Stack>
    {children}
  </TasteSectionCard>
);

export const Support = () => {
  const { headerHeight } = useAppLayout();
  const [query, setQuery] = useState('');
  const [activeId, setActiveId] = useState('');

  usePageMeta(
    'Support',
    'Step-by-step guides for securing your account, adding properties, budgeting, getting approved, and submitting offers on Unbeatable Loans.'
  );

  const scrollOffset = headerHeight + NAV_RAIL_GAP;
  const normalizedQuery = query.trim().toLowerCase();

  const visibleGroups = useMemo(() => {
    if (!normalizedQuery) return supportGuideGroups;
    return supportGuideGroups
      .map((group) => ({
        ...group,
        guides: group.guides.filter((guide) =>
          guideSearchText(guide).includes(normalizedQuery)
        ),
      }))
      .filter((group) => group.guides.length > 0);
  }, [normalizedQuery]);

  const showCosts =
    !normalizedQuery || costsSearchText().includes(normalizedQuery);
  const hasResults = visibleGroups.length > 0 || showCosts;

  const sectionIds = useMemo(() => {
    const ids = visibleGroups.flatMap((group) =>
      group.guides.map((guide) => guide.id)
    );
    return showCosts ? [...ids, COSTS_SECTION_ID] : ids;
  }, [visibleGroups, showCosts]);

  const sectionIdKey = sectionIds.join('|');

  // The active topic is the last section whose top has passed the header line.
  useEffect(() => {
    const ids = sectionIdKey.split('|').filter(Boolean);
    if (!ids.length) {
      setActiveId('');
      return undefined;
    }

    const syncActive = () => {
      let current = ids[0];
      ids.forEach((id) => {
        const rect = document.getElementById(id)?.getBoundingClientRect();
        if (rect && rect.top <= scrollOffset + 8) current = id;
      });
      setActiveId(current);
    };

    syncActive();
    // Second pass, once a deep-link scroll or late layout has settled.
    const frame = window.requestAnimationFrame(syncActive);
    window.addEventListener('scroll', syncActive, { passive: true });
    window.addEventListener('resize', syncActive);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', syncActive);
      window.removeEventListener('resize', syncActive);
    };
  }, [sectionIdKey, scrollOffset]);

  // The browser cannot resolve the hash on first paint: the sections are
  // client-rendered and do not exist yet when the document loads.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    scrollToSection(id);
  }, []);

  const navGroups = [
    ...visibleGroups.map((group) => ({
      label: group.label,
      items: group.guides.map((guide) => ({
        id: guide.id,
        label: guide.navLabel,
      })),
    })),
    ...(showCosts
      ? [
          {
            label: 'Money',
            items: [{ id: COSTS_SECTION_ID, label: 'Costs involved' }],
          },
        ]
      : []),
  ];

  return (
    <Box>
      <Box
        sx={{
          background:
            'linear-gradient(135deg, color-mix(in srgb, var(--primary-navy) 7%, transparent) 0%, color-mix(in srgb, var(--surface-container-lowest) 85%, transparent) 100%)',
          borderBottom:
            '1px solid color-mix(in srgb, var(--outline-variant) 45%, transparent)',
        }}
      >
        <Container sx={{ py: { xs: 5, md: 8 } }}>
          <Box className="taste-rise" sx={{ maxWidth: 720 }}>
            <Typography
              variant="overline"
              sx={{ color: 'var(--on-surface-variant)', fontWeight: 700 }}
            >
              Support
            </Typography>
            <Typography
              variant="h3"
              component="h1"
              sx={{
                mt: 1,
                fontFamily: 'var(--font-display)',
                fontSize: { xs: '2rem', md: '2.75rem' },
                fontWeight: 700,
                letterSpacing: '-0.02em',
                color: 'var(--primary-navy)',
              }}
            >
              How can we help?
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mt: 1.5,
                color: 'var(--on-surface-variant)',
                fontSize: { xs: '1rem', md: '1.125rem' },
                lineHeight: 1.65,
              }}
            >
              Walkthroughs for every step of buying with Unbeatable Loans, from
              securing your account to signing an offer. If a guide does not
              answer it, a real person will.
            </Typography>

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              sx={{ mt: 4 }}
            >
              <Button
                href={SUPPORT_PHONE_HREF}
                variant="contained"
                startIcon={<i className="fa-light fa-phone" />}
                sx={{ px: 3, py: 1.5 }}
              >
                {SUPPORT_PHONE}
              </Button>
              <Button
                href={SUPPORT_EMAIL_HREF}
                variant="outlined"
                startIcon={<i className="fa-light fa-envelope" />}
                sx={{ px: 3, py: 1.5 }}
              >
                Email support
              </Button>
            </Stack>
          </Box>

          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search help topics"
            type="search"
            aria-label="Search help topics"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <i
                    className="fa-light fa-magnifying-glass"
                    aria-hidden="true"
                  />
                </InputAdornment>
              ),
            }}
            sx={{
              mt: 4,
              maxWidth: 480,
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'var(--surface-container-lowest)',
                boxShadow:
                  'inset 0 0 0 1px color-mix(in srgb, var(--outline-variant) 55%, transparent)',
              },
              '& .MuiInputAdornment-root': {
                color: 'var(--on-surface-variant)',
              },
            }}
          />
        </Container>
      </Box>

      <Container sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={{ xs: 3, md: 5 }}>
          <Grid
            item
            xs={12}
            md={3}
            sx={{ display: hasResults ? 'block' : 'none' }}
          >
            {/* Phones get a compact chip row; a stacked rail would push the
                first guide most of a screen down. */}
            <Box
              component="nav"
              aria-label="Support topics"
              sx={{
                display: { xs: 'flex', md: 'none' },
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              {navGroups
                .flatMap((group) => group.items)
                .map((item) => {
                  const isActive = item.id === activeId;
                  return (
                    <MuiLink
                      key={item.id}
                      href={`#${item.id}`}
                      underline="none"
                      aria-current={isActive ? 'true' : undefined}
                      sx={{
                        px: 1.5,
                        py: 0.75,
                        borderRadius: '9999px',
                        fontSize: '0.8125rem',
                        fontWeight: 600,
                        color: isActive
                          ? 'var(--primary-navy)'
                          : 'var(--on-surface-variant)',
                        backgroundColor: isActive
                          ? 'color-mix(in srgb, var(--primary-fixed) 70%, transparent)'
                          : 'var(--surface-container-lowest)',
                        boxShadow:
                          'inset 0 0 0 1px color-mix(in srgb, var(--outline-variant) 45%, transparent)',
                      }}
                    >
                      {item.label}
                    </MuiLink>
                  );
                })}
            </Box>

            <Box
              component="nav"
              aria-label="Support topics"
              sx={{
                display: { xs: 'none', md: 'block' },
                position: 'sticky',
                top: `${scrollOffset}px`,
              }}
            >
              {navGroups.map((group) => (
                <Box key={group.label} sx={{ mb: 2.5 }}>
                  <Typography
                    variant="overline"
                    sx={{
                      display: 'block',
                      mb: 0.75,
                      color: 'var(--on-surface-variant)',
                      fontWeight: 700,
                    }}
                  >
                    {group.label}
                  </Typography>
                  <Stack spacing={0.25}>
                    {group.items.map((item) => {
                      const isActive = item.id === activeId;
                      return (
                        <MuiLink
                          key={item.id}
                          href={`#${item.id}`}
                          underline="none"
                          aria-current={isActive ? 'true' : undefined}
                          sx={{
                            display: 'block',
                            px: 1.5,
                            py: 0.875,
                            borderRadius: '8px',
                            borderLeft: '2px solid',
                            borderLeftColor: isActive
                              ? 'var(--primary-navy)'
                              : 'transparent',
                            fontSize: '0.875rem',
                            fontWeight: isActive ? 700 : 500,
                            color: isActive
                              ? 'var(--primary-navy)'
                              : 'var(--on-surface-variant)',
                            backgroundColor: isActive
                              ? 'color-mix(in srgb, var(--primary-fixed) 45%, transparent)'
                              : 'transparent',
                            transition: 'background-color var(--motion-soft)',
                            '&:hover': {
                              backgroundColor:
                                'color-mix(in srgb, var(--primary-fixed) 30%, transparent)',
                              color: 'var(--primary-navy)',
                            },
                          }}
                        >
                          {item.label}
                        </MuiLink>
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Box>
          </Grid>

          <Grid item xs={12} md={hasResults ? 9 : 12}>
            <Stack spacing={{ xs: 2.5, md: 3 }} sx={{ maxWidth: 820 }}>
              {visibleGroups.flatMap((group) =>
                group.guides.map((guide) => (
                  <SectionCard
                    key={guide.id}
                    id={guide.id}
                    icon={guide.icon}
                    title={guide.title}
                    summary={guide.summary}
                    scrollOffset={scrollOffset}
                  >
                    <StepList guide={guide} />
                  </SectionCard>
                ))
              )}

              {showCosts ? (
                <SectionCard
                  id={COSTS_SECTION_ID}
                  icon="fa-receipt"
                  title="Costs involved"
                  summary="What you pay for along the way, and when each one comes due."
                  scrollOffset={scrollOffset}
                >
                  <Box>
                    {supportCosts.map((cost, index) => (
                      <Box
                        key={cost.name}
                        sx={{
                          display: 'flex',
                          flexDirection: { xs: 'column', sm: 'row' },
                          alignItems: { xs: 'flex-start', sm: 'center' },
                          gap: { xs: 1, sm: 3 },
                          py: 2,
                          borderTop:
                            index === 0
                              ? 'none'
                              : '1px solid color-mix(in srgb, var(--outline-variant) 35%, transparent)',
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body1"
                            sx={{
                              fontWeight: 700,
                              color: 'var(--on-surface)',
                            }}
                          >
                            {cost.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              mt: 0.25,
                              color: 'var(--on-surface-variant)',
                              lineHeight: 1.6,
                            }}
                          >
                            {cost.description}
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            flex: '0 0 auto',
                            px: 1.25,
                            py: 0.5,
                            borderRadius: '9999px',
                            fontSize: '0.75rem',
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            color: 'var(--primary-navy)',
                            backgroundColor:
                              'color-mix(in srgb, var(--primary-fixed) 60%, transparent)',
                          }}
                        >
                          {cost.when}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </SectionCard>
              ) : null}

              {hasResults ? null : (
                <TasteSectionCard
                  sx={{
                    p: { xs: 3, md: 5 },
                    textAlign: 'center',
                    backgroundColor:
                      'color-mix(in srgb, var(--surface-container-lowest) 90%, var(--primary-fixed))',
                    border:
                      '1px dashed color-mix(in srgb, var(--outline-variant) 70%, transparent)',
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontFamily: 'var(--font-display)',
                      fontWeight: 700,
                      color: 'var(--primary-navy)',
                    }}
                  >
                    {`No guide matches “${query.trim()}”`}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: 'var(--on-surface-variant)' }}
                  >
                    Clear the search to browse every topic, or ask us directly
                    at{' '}
                    <MuiLink href={SUPPORT_EMAIL_HREF} underline="hover">
                      {SUPPORT_EMAIL}
                    </MuiLink>
                    .
                  </Typography>
                  <Button
                    variant="outlined"
                    onClick={() => setQuery('')}
                    sx={{ mt: 3, px: 3, py: 1.25 }}
                  >
                    Clear search
                  </Button>
                </TasteSectionCard>
              )}

              {hasResults ? (
                <Box
                  sx={{
                    mt: { xs: 1, md: 2 },
                    px: { xs: 2.5, md: 4 },
                    py: { xs: 3, md: 3.5 },
                    borderRadius: 'calc(var(--radius) + 2px)',
                    background:
                      'linear-gradient(135deg, color-mix(in srgb, var(--primary-navy) 8%, transparent) 0%, color-mix(in srgb, var(--surface-container-lowest) 90%, transparent) 100%)',
                    border:
                      '1px solid color-mix(in srgb, var(--outline-variant) 45%, transparent)',
                  }}
                >
                  <Typography
                    variant="h5"
                    component="h2"
                    sx={{
                      fontFamily: 'var(--font-display)',
                      fontSize: '1.125rem',
                      fontWeight: 700,
                      color: 'var(--primary-navy)',
                    }}
                  >
                    Still stuck?
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      mt: 1,
                      color: 'var(--on-surface-variant)',
                      lineHeight: 1.65,
                    }}
                  >
                    Call {SUPPORT_PHONE} or email{' '}
                    <MuiLink
                      href={SUPPORT_EMAIL_HREF}
                      underline="hover"
                      sx={{ fontWeight: 600 }}
                    >
                      {SUPPORT_EMAIL}
                    </MuiLink>
                    . Include a screenshot and the property or contract ID and
                    we can pick up exactly where you left off.
                  </Typography>
                </Box>
              ) : null}
            </Stack>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};
